import { KeoChapView } from "../../../helpers/dieuKhoanTran";
import { useLayoutEffect, useRef, useState } from "react";

import { initials } from "./Avatar";
import { LeagueMatchView } from "./types";

/**
 * Những mảnh giao diện mà mọi trang giải của front office cùng dùng.
 *
 * Chuyển NGUYÊN VĂN từ swiss/live.tsx sang đây, chỉ đổi đúng hai thứ: thêm `export`,
 * và kiểu của cặp đấu từ `SwissMatch` thành `LeagueMatchView` (xem ./types) để trang
 * Đấu kép dùng được mà không phải khai dữ liệu của mình là "Swiss".
 *
 * VÌ SAO DÙNG CHUNG CHỨ KHÔNG CHÉP: yêu cầu sản phẩm là trang Đấu kép phải nhất quán
 * với trang Swiss. Nhất quán bằng cách chia sẻ code thì đứng vững; nhất quán bằng cách
 * chép thì lệch ngay lần sửa đầu tiên - và lệch ở chỗ không ai nhìn, vì hai trang khác
 * nhau thì không ai mở cạnh nhau để so.
 */

export const STATUS_META: Record<string, { cls: string; txt: string; dot?: boolean }> = {
  LIVE: { cls: "live", txt: "Đang đấu", dot: true },
  DRAFT: { cls: "wait", txt: "Chờ xác nhận" },
  CONFIRMED: { cls: "done", txt: "Đã xác nhận" },
  BYE: { cls: "red", txt: "Miễn đấu" },
};

export const You = () => <span className="you">BẠN</span>;

/* Ghi chú thứ hạng chung cuộc mà backend trả về cho người thắng chung kết. Đây là
   chuỗi duy nhất phân biệt nhà vô địch với "Á quân"/"Dừng ở bán kết", nên để một
   chỗ thay vì gõ lại ở ba nơi. */
export const VO_DICH = "Vô địch";

/**
 * Cúp gắn cạnh tên nhà vô địch.
 *
 * Luôn là anh em của .bn/.nm chứ không nằm trong: hai lớp đó cắt chữ bằng
 * text-overflow, đặt cúp bên trong thì một cái tên dài nuốt mất cúp.
 */
export const CupVoDich = () => (
  <span className="cupvd" role="img" aria-label={VO_DICH} title={VO_DICH}>
    🏆
  </span>
);

/**
 * Tên cơ thủ trong bảng xếp hạng: cho xuống TỐI ĐA hai dòng theo bề rộng cột tên,
 * và chỉ khi hai dòng vẫn không đủ mới cắt GIỮA (đầu + "…" + đuôi). Cắt giữa để
 * phần đuôi phân biệt ("… Nguyên", "… Dũng") không mất như khi cắt cuối.
 *
 * Phải đo bằng JS: `-webkit-line-clamp` gói được hai dòng nhưng chỉ đặt ba chấm ở
 * CUỐI, không có cách thuần CSS nào cắt giữa. Cách làm: dựng khung cao đúng hai dòng
 * (CSS `.nm2`), nếu nội dung đầy đủ tràn khung (scrollHeight > clientHeight) thì tìm
 * nhị phân độ dài giữ lại lớn nhất mà vẫn khít. Cột tên co giãn theo vỏ máy nên quan
 * sát ResizeObserver để đo lại khi bề rộng đổi.
 */

/**
 * Tên cơ thủ trong bảng xếp hạng: cho xuống TỐI ĐA hai dòng theo bề rộng cột tên,
 * và chỉ khi hai dòng vẫn không đủ mới cắt GIỮA (đầu + "…" + đuôi). Cắt giữa để
 * phần đuôi phân biệt ("… Nguyên", "… Dũng") không mất như khi cắt cuối.
 *
 * Phải đo bằng JS: `-webkit-line-clamp` gói được hai dòng nhưng chỉ đặt ba chấm ở
 * CUỐI, không có cách thuần CSS nào cắt giữa. Cách làm: dựng khung cao đúng hai dòng
 * (CSS `.nm2`), nếu nội dung đầy đủ tràn khung (scrollHeight > clientHeight) thì tìm
 * nhị phân độ dài giữ lại lớn nhất mà vẫn khít. Cột tên co giãn theo vỏ máy nên quan
 * sát ResizeObserver để đo lại khi bề rộng đổi.
 */
export const PlayerName = ({ name }: { name: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [text, setText] = useState(name);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fits = () => el.scrollHeight <= el.clientHeight + 1;
    const build = (k: number) => {
      const head = Math.ceil(k / 2);
      const tail = Math.floor(k / 2);
      return name.slice(0, head) + "…" + name.slice(name.length - tail);
    };

    const run = () => {
      el.textContent = name;
      if (fits()) {
        setText(name);
        return;
      }
      let lo = 0;
      let hi = name.length - 1;
      let best = "…";
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const candidate = build(mid);
        el.textContent = candidate;
        if (fits()) {
          best = candidate;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      // Trả DOM về đúng giá trị React sắp render, tránh lệch một khung hình.
      el.textContent = best;
      setText(best);
    };

    run();
    const ro = new ResizeObserver(run);
    ro.observe(el);
    return () => ro.disconnect();
  }, [name]);

  return (
    <span className="nm2" ref={ref} title={name}>
      {text}
    </span>
  );
};

export const Balls = ({ lives, total }: { lives: number; total: number }) => (
  <div className="balls">
    {Array.from({ length: total }, (_, i) => (
      <div key={i} className={`ball ${i < lives ? "" : "gone"}`}>
        8
      </div>
    ))}
  </div>
);

/**
 * Dòng kèo trên thẻ cặp đấu.
 *
 * Tên hai cơ thủ để màu chữ thường và bọc ngoặc vuông, còn chữ "chấp" và phần số thì
 * màu nhấn: mắt bắt được AI CHẤP AI trước, rồi mới tới bao nhiêu. Một dòng cùng một
 * màu thì phải đọc hết cả câu mới biết chiều.
 *
 * Con số in đậm vì đó là thứ người ta soi: "2 ván 1 bi" và "1 ván 2 bi" khác nhau, mà
 * hai chuỗi đó trông gần như nhau khi lướt qua.
 *
 * Đánh đồng cũng in một dòng, để mọi thẻ cao bằng nhau - thẻ có dòng kèo đứng cạnh
 * thẻ không có thì mắt đọc thành hai loại trận khác nhau. Nhưng trận CHƯA báo giá thì
 * chỗ gọi không dựng component này: chưa có câu trả lời nào thì "Đánh đồng" là một
 * kết luận chưa ai đưa ra.
 */
const DongKeo = ({ keo }: { keo: KeoChapView }) => {
  if (!keo.van && !keo.bi) {
    return <div className="mkeo mkeo-dong">Đánh đồng</div>;
  }

  return (
    <div className="mkeo">
      <span className="mkeo-ten">[{keo.tenNguoiChap}]</span>{" "}
      <span className="mkeo-nhan">chấp</span>{" "}
      <span className="mkeo-ten">[{keo.tenNguoiDuocChap}]</span>{" "}
      <span className="mkeo-nhan">
        {!!keo.van && (
          <>
            <b>{keo.van}</b> ván
          </>
        )}
        {!!keo.van && !!keo.bi && " "}
        {!!keo.bi && (
          <>
            <b>{keo.bi}</b> bi
          </>
        )}
      </span>
    </div>
  );
};

export const MatchRow = ({ match, myPlayerId }: { match: LeagueMatchView; myPlayerId: string | null }) => {
  const meta = STATUS_META[match.status] || STATUS_META.LIVE;
  const mine = match.mine ? " mine" : "";

  if (match.isBye) {
    const player = match.players[0];
    return (
      <div className={`mrow${mine}`}>
        <div className="mtop">
          <span className="mtbl" style={{ color: "var(--red)" }}>
            MIỄN ĐẤU
          </span>
          <span className="mstat">
            <span className="chip red">Miễn đấu</span>
          </span>
        </div>
        <div className="pline">
          <div className={`avas ${player?.id === myPlayerId ? "me" : ""}`}>{initials(player?.name || "")}</div>
          {/* Nhãn BẠN đứng NGOÀI .pn: nằm trong thì tên dài bị ellipsis nuốt luôn
              nhãn, đúng lúc người đọc cần nó nhất. */}
          <div className="pn">{player?.name}</div>
          {player?.id === myPlayerId && <You />}
          <div className="ps">+1</div>
        </div>
        <div className="byebox">
          Do số người còn lại là số lẻ. Được tính +1 thắng, không mất mạng và vào thẳng vòng tiếp theo.
        </div>
      </div>
    );
  }

  const decided = match.status === "CONFIRMED";
  // Người thắng hiển thị trên; khi chưa có kết quả thì giữ nguyên thứ tự bốc cặp.
  const ordered = decided ? [...match.players].sort((a, b) => Number(b.isWinner) - Number(a.isWinner)) : match.players;

  return (
    <div className={`mrow${mine}`}>
      <div className="mtop">
        <span className="mtbl">{match.table ? `BÀN ${match.table}` : "CHƯA XẾP BÀN"}</span>
        <span className="mmeta">
          {match.venueName && (
            <>
              <span className="dot mut" />
              {match.venueName}
            </>
          )}
          <span className="dot mut" />
          Chạm {match.race}
        </span>
        <span className="mstat">
          <span className={`chip ${meta.cls}`}>
            {meta.dot && <span className="pulse" />}
            {meta.txt}
          </span>
        </span>
      </div>
      {/* Kèo của chính trận này, kể cả khi đánh đồng - mọi thẻ cao bằng nhau thì mắt
          không đọc thành hai loại trận. Trận CHƯA báo giá vẫn không in gì: chưa có
          câu trả lời nào thì in "Đánh đồng" là bịa ra một kết luận chưa ai đưa ra. */}
      {match.keo && <DongKeo keo={match.keo} />}
      {ordered.map((player, index) => (
        <div key={player.id ?? index} className={`pline ${decided && player.isWinner ? "win" : ""}`}>
          <div className={`avas ${player.id === myPlayerId ? "me" : ""}`}>{initials(player.name)}</div>
          <div className="pn">{player.name}</div>
          {player.id === myPlayerId && <You />}
          <div className="ps">{player.score === null || player.score === undefined ? "—" : player.score}</div>
        </div>
      ))}
      {match.status === "DRAFT" && (
        <div className="mfoot">
          <span className="tiny">Đã có người nhập kết quả • chờ đối phương xác nhận</span>
        </div>
      )}
    </div>
  );
};

export const BracketNode = ({
  match,
  myPlayerId,
  laChungKet = false,
}: {
  match: LeagueMatchView;
  myPlayerId: string | null;
  /** Trận này là chung kết — người thắng ở đây mới là vô địch. */
  laChungKet?: boolean;
}) => {
  const seat = (index: number) => {
    const player = match.players[index];
    if (!player || !player.id) {
      // Ghế trống: cặp phía dưới chưa đấu xong nên chưa biết ai vào đây.
      return (
        <div className="bseat empty">
          <span className="bn">Chờ kết quả</span>
          <span className="bs">—</span>
        </div>
      );
    }
    const isMe = !!myPlayerId && player.id === myPlayerId;

    return (
      <div className={`bseat ${player.isWinner ? "win" : ""} ${isMe ? "me" : ""}`}>
        <span className="bn">{player.name || "—"}</span>
        {laChungKet && player.isWinner ? <CupVoDich /> : null}
        <span className="bs">{player.score === null ? "—" : player.score}</span>
      </div>
    );
  };

  return (
    <div className={`bnode ${match.mine ? "mine" : ""}`}>
      {seat(0)}
      {seat(1)}
    </div>
  );
};
